/**
 * Cross-tab synchronization channel for Savanna X AgriTech.
 *
 * Broadcasts lightweight messages between browser tabs/windows on the same
 * origin so that auth, language, cart, and profile state stay in sync without
 * requiring a logout/login or a manual refresh.
 *
 * Falls back to a `localStorage` event ping when `BroadcastChannel` is
 * unavailable (older Safari, some embedded webviews).
 */

export type CrossTabEvent =
  | { type: 'auth:signout' }
  | { type: 'auth:signin'; userId: string }
  | { type: 'language:changed'; language: string }
  | { type: 'profile:updated'; userId: string }
  | { type: 'cart:changed' }
  | { type: 'data:invalidate'; scope: string };

const CHANNEL_NAME = 'sxa-sync';
const FALLBACK_KEY = 'sxa-sync-ping';

type Listener = (evt: CrossTabEvent) => void;
const listeners = new Set<Listener>();

let bc: BroadcastChannel | null = null;

function ensureChannel() {
  if (typeof window === 'undefined') return;
  if (bc) return;

  if ('BroadcastChannel' in window) {
    try {
      bc = new BroadcastChannel(CHANNEL_NAME);
      bc.onmessage = (e) => {
        const data = e.data as CrossTabEvent | undefined;
        if (data && typeof data === 'object' && 'type' in data) {
          listeners.forEach((l) => {
            try { l(data); } catch { /* noop */ }
          });
        }
      };
    } catch {
      bc = null;
    }
  }

  // Always also listen to storage events as a fallback for tabs without BC.
  window.addEventListener('storage', (e) => {
    if (e.key !== FALLBACK_KEY || !e.newValue) return;
    try {
      const data = JSON.parse(e.newValue) as CrossTabEvent;
      listeners.forEach((l) => {
        try { l(data); } catch { /* noop */ }
      });
    } catch { /* noop */ }
  });
}

export function broadcast(evt: CrossTabEvent) {
  if (typeof window === 'undefined') return;
  ensureChannel();

  if (bc) {
    try { bc.postMessage(evt); } catch { /* noop */ }
  }
  // Storage-event fallback (also helps when BC is blocked by partitioning).
  try {
    localStorage.setItem(FALLBACK_KEY, JSON.stringify({ ...evt, _t: Date.now() }));
  } catch { /* quota — non-fatal */ }
}

export function subscribe(listener: Listener): () => void {
  ensureChannel();
  listeners.add(listener);
  return () => listeners.delete(listener);
}
